import type { WaveConfig, Position } from "@/types/game.types"
import type { Enemy } from "@/types/enemy.types"
import { createEnemy } from "./enemy-system"

export interface WaveSpawner {
  isActive: boolean
  waveConfig: WaveConfig | null
  spawnPosition: Position
  enemiesToSpawn: Array<{ type: string; spawnFrame: number }>
  currentFrame: number
  totalSpawned: number
  waveReward: number
  waveRewardClaimed: boolean
  progressionLevel: number // Level bonus from starmap progression
  nodesCompleted: number // Number of starmap nodes completed (for aggro disable)
}

export function createWaveSpawner(): WaveSpawner {
  return {
    isActive: false,
    waveConfig: null,
    spawnPosition: { x: 0, y: 0 },
    enemiesToSpawn: [],
    currentFrame: 0,
    totalSpawned: 0,
    waveReward: 0,
    waveRewardClaimed: false,
    progressionLevel: 0,
    nodesCompleted: 0,
  }
}

/**
 * Start a wave - builds the spawn list with frame-based timing
 * Similar to the working example's spawnEnemies but with staggered spawns
 * progressionLevel: bonus level from starmap progression (0-7), passed to enemies
 * nodesCompleted: number of starmap nodes completed (for aggro disable in first 2 levels)
 */
export function startWave(waveConfig: WaveConfig, spawnPosition: Position, progressionLevel: number = 0, nodesCompleted: number = 0): WaveSpawner {
  const enemiesToSpawn: Array<{ type: string; spawnFrame: number }> = []

  let frameOffset = 0

  // Build spawn list from all enemy groups
  for (const group of waveConfig.enemies) {
    for (let i = 0; i < group.count; i++) {
      enemiesToSpawn.push({
        type: group.type,
        spawnFrame: frameOffset + i * group.spawnInterval,
      })
    }
    // Offset next group to start after this one
    frameOffset += group.count * group.spawnInterval
  }

  // Sort by spawn frame
  enemiesToSpawn.sort((a, b) => a.spawnFrame - b.spawnFrame)

  console.log("[v0] startWave: Created wave with", enemiesToSpawn.length, "enemies to spawn")
  console.log("[v0] startWave: First enemy spawns at frame", enemiesToSpawn[0]?.spawnFrame)
  console.log("[v0] startWave: Spawn position:", spawnPosition, "progressionLevel:", progressionLevel, "nodesCompleted:", nodesCompleted)

  return {
    isActive: true,
    waveConfig,
    spawnPosition,
    enemiesToSpawn,
    currentFrame: 0,
    totalSpawned: 0,
    waveReward: waveConfig.reward,
    waveRewardClaimed: false,
    progressionLevel,
    nodesCompleted,
  }
}

/**
 * Update the wave spawner each frame
 * Simple approach: increment frame counter, spawn enemies whose frame has arrived
 */
export function updateWaveSpawner(spawner: WaveSpawner, onSpawn: (enemy: Enemy) => void): WaveSpawner {
  // If not active, don't process
  if (!spawner.isActive) {
    return spawner
  }

  // If no enemies left to spawn, deactivate
  if (spawner.enemiesToSpawn.length === 0) {
    return { ...spawner, isActive: false }
  }

  // Increment frame counter
  const currentFrame = spawner.currentFrame + 1

  // Find all enemies ready to spawn this frame
  const readyToSpawn = spawner.enemiesToSpawn.filter((e) => e.spawnFrame <= currentFrame)
  const remaining = spawner.enemiesToSpawn.filter((e) => e.spawnFrame > currentFrame)

  // Spawn enemies with progression level and nodesCompleted
  let totalSpawned = spawner.totalSpawned
  for (const item of readyToSpawn) {
    const enemy = createEnemy(item.type, spawner.spawnPosition, spawner.progressionLevel, spawner.nodesCompleted)
    onSpawn(enemy)
    totalSpawned++
  }

  return {
    ...spawner,
    currentFrame,
    enemiesToSpawn: remaining,
    totalSpawned,
    isActive: remaining.length > 0,
  }
}

export function isWaveComplete(spawner: WaveSpawner, enemiesAlive: number): boolean {
  return !spawner.isActive && spawner.totalSpawned > 0 && enemiesAlive === 0
}
